import { isBefore, isValid, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';

import { isDesktop } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import { useSharedStateSelector } from '@trello/shared-state';
import { workspaceState } from '@trello/workspace-state';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';

export const useHasEndOfTrialFriction = () => {
  /* -- STATE -- */
  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();
  /* -- HOOKS -- */
  const workspaceId =
    useSharedStateSelector(
      workspaceState,
      useCallback((state) => state?.workspaceId, []),
    ) || '';

  const FREE_TRIAL_BANNER_DISMISS_MESSAGE = `free-trial-banner-${workspaceId}`;

  const {
    endDate,
    isAdmin,
    isStandard,
    isPremium,
    isTrialExtended,
    isEnterpriseWorkspace,
  } = useFreeTrialEligibilityRules(workspaceId, {
    skip: !workspaceId.length,
  });

  const { experimentStartDate } = useGetExperimentValue({
    experimentName: 'trello_enterprise_trial_ended_friction',
    parameter: 'experimentStartDate',
    fireExposureEvent: false,
  });
  /* -- MEMOIZED STATE -- */

  const freeTrialBannerDismissed = isOneTimeMessageDismissed(
    FREE_TRIAL_BANNER_DISMISS_MESSAGE,
  );

  const shouldFireExposureEvent = useMemo(() => {
    // do not show experience if the workspace is currently on a paid entitlement
    if (isStandard || isPremium) {
      return false;
    }
    // It handles the rare case in the email verification flow where, when we start the reverse-trial, `isPremium` is false.
    // we need to manually check if the end date is not in the past
    if (!endDate) {
      return false;
    }
    if (isBefore(new Date(Date.now()), endDate)) {
      return false;
    }
    // if the user has already extended their trial, they should not be eligible
    if (isTrialExtended) {
      return false;
    }
    // no desktop app
    if (isDesktop()) {
      return false;
    }
    // exclude users who have dismissed the free trial banner from being enrolled in the experiment
    if (freeTrialBannerDismissed) {
      return false;
    }
    // must be an admin
    if (!isAdmin) {
      return false;
    }
    // When workspace is in reverse trial being added into enterprise, the premium trial will be canceled
    // and we should not display end of trial friction model for that workspace.
    if (isEnterpriseWorkspace) {
      return false;
    }

    const parsedStartDate = parseISO(experimentStartDate);

    if (!isValid(parsedStartDate)) {
      return false;
    }
    // only workspaces whose free trial expires after the experiment starts will be eligible
    if (endDate && isBefore(endDate, parsedStartDate)) {
      return false;
    }

    return true;
  }, [
    endDate,
    experimentStartDate,
    freeTrialBannerDismissed,
    isAdmin,
    isEnterpriseWorkspace,
    isPremium,
    isStandard,
    isTrialExtended,
  ]);
  const { cohort } = useGetExperimentValue({
    experimentName: 'trello_enterprise_trial_ended_friction',
    parameter: 'cohort',
    fireExposureEvent: shouldFireExposureEvent,
  });

  const hasEndOfTrialFriction =
    shouldFireExposureEvent && cohort === 'experiment';

  return hasEndOfTrialFriction;
};
